import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Avatar, Button, Card, Chip, EmptyState, Icon, Skeleton, Tabs, fmtNum } from "../components/ui";
import { personalize } from "../components/CharacterCard";
import { Cover, FLAG_LABEL, statusLabel } from "../components/character/Card";
import ShareModal from "../components/character/ShareModal";
import Comments from "../components/social/Comments";
import PostCard from "../components/social/PostCard";
import ReportModal from "../components/social/ReportModal";
import useMe from "../components/social/useMe";
import { useAsync } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useI18n } from "../services/i18n";
import api from "../services/api";
import content from "../services/api-content";
import "../styles/content.css";

const dateOf = (iso) => (iso && !Number.isNaN(new Date(iso).getTime()) ? new Date(iso).toLocaleDateString("th-TH", { dateStyle: "medium" }) : "");

function Gallery({ c }) {
  const imgs = [c.image, ...(c.images || [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
  const [i, setI] = useState(0);
  const cur = { ...c, image: imgs[i] || "" };
  return (
    <div style={{ position: "relative", maxWidth: 420, margin: "0 auto" }}>
      <Cover c={cur} size="med" eager style={{ fontSize: 96, width: "100%", aspectRatio: "3 / 4" }} />
      {imgs.length > 1 ? (
        <>
          <Button size="sm" icon variant="ghost" aria-label="รูปก่อนหน้า" onClick={() => setI((i - 1 + imgs.length) % imgs.length)} style={{ position: "absolute", left: 8, top: "45%", background: "rgba(0,0,0,.5)" }}><Icon name="back" /></Button>
          <Button size="sm" icon variant="ghost" aria-label="รูปถัดไป" onClick={() => setI((i + 1) % imgs.length)} style={{ position: "absolute", right: 8, top: "45%", background: "rgba(0,0,0,.5)", transform: "scaleX(-1)" }}><Icon name="back" /></Button>
          <span className="badge" style={{ position: "absolute", right: 10, bottom: 10, zIndex: 2 }}>{i + 1}/{imgs.length}</span>
        </>
      ) : null}
    </div>
  );
}

function GiftRanking({ id }) {
  const { data, loading, error } = useAsync(() => content.characters.giftRanking(id), [id]);
  if (error) return <div className="error-text">{error}</div>;
  if (loading) return <Skeleton height={120} />;
  if (!data.items.length) return <EmptyState icon="🎁" title="ยังไม่มีใครส่งของขวัญ">เป็นคนแรกที่ส่งของขวัญให้ตัวละครนี้ในห้องแชท</EmptyState>;
  return (
    <div className="stack">
      {data.items.map((r) => (
        <div key={r.rank} className="row"><span className={`rank-n${r.rank <= 3 ? " top" : ""}`}>{r.rank}</span><Avatar name={r.name} src={r.photoURL} size={36} /><div className="grow strong clamp-1">{r.name}</div><span className="coin"><i className="coin__dot" />{fmtNum(r.coins)}</span></div>
      ))}
    </div>
  );
}

function PostsTab({ id, type, meId, isSuper }) {
  const { data, loading, error, reload } = useAsync(() => content.posts.list({ type, characterId: id }), [id, type]);
  if (error) return <div className="error-text">{error}</div>;
  if (loading) return <Skeleton height={120} />;
  if (!data.items.length) return <EmptyState icon={type === "short" ? "🎬" : "📝"} title={type === "short" ? "ยังไม่มีคลิปสั้น" : "ยังไม่มีโพสต์"}>ตัวละครนี้ยังไม่ถูกแท็กในเนื้อหา</EmptyState>;
  return <div className="stack">{data.items.map((p) => <PostCard key={p.id} p={p} meId={meId} isSuper={isSuper} onDeleted={reload} />)}</div>;
}

export default function Character() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useI18n();
  const { profile, isSuperUser } = useAuth();
  const [tab, setTab] = useState("info");
  const [starting, setStarting] = useState(false);
  const [share, setShare] = useState(false);
  const [report, setReport] = useState(false);
  const [commentCount, setCommentCount] = useState(null);
  const { data: c, loading, error, reload } = useAsync(() => content.characters.get(id), [id]);
  const me = useMe();
  const prof = useAsync(() => (c && !c.isOwner && c.creatorId ? content.creators.profile(c.creatorId).catch(() => null) : Promise.resolve(null)), [c?.creatorId]);
  const [local, setLocal] = useState({});
  const state = (k, fallback) => (local[`${id}:${k}`] ?? fallback);
  const setState = (k, v) => setLocal((s) => ({ ...s, [`${id}:${k}`]: v }));

  if (error) return <EmptyState icon="😶" title="เปิดตัวละครไม่ได้" action={<Button onClick={() => navigate("/")}>กลับหน้าแรก</Button>}>{error}</EmptyState>;
  if (loading) return <div className="stack" style={{ maxWidth: 900, margin: "0 auto" }}><Skeleton height={320} radius={24} /><Skeleton height={28} width="50%" /><Skeleton height={60} /></div>;

  const isLiked = state("liked", !!c.isLiked);
  const likeCount = state("likes", Number(c.likeCount) || 0);
  const isSaved = state("saved", !!c.isSaved);
  const following = state("following", !!prof.data?.isFollowing);
  const meId = me?.id;
  const isOwner = !!c.isOwner;

  const start = async () => {
    setStarting(true);
    try {
      const chat = await api.chats.create(c.id);
      navigate(`/chat/${chat.id}`);
    } catch (e) {
      toast(e.message, "error");
      setStarting(false);
    }
  };
  const like = async () => {
    try { const r = await content.characters.like(id); setState("liked", r.liked); setState("likes", r.likeCount ?? likeCount + (r.liked ? 1 : -1)); } catch (e) { toast(e.message, "error"); }
  };
  const save = async () => {
    try { const r = await content.characters.toggleSave(id); setState("saved", r.liked); toast(r.liked ? "บันทึกตัวละครแล้ว" : "ยกเลิกการบันทึกแล้ว", "success"); } catch (e) { toast(e.message, "error"); }
  };
  const follow = async () => {
    try { const r = await content.creators.follow(c.creatorId); setState("following", r.following); } catch (e) { toast(e.message, "error"); }
  };
  const toggleArchive = async () => {
    try { await content.characters.archive(id, !c.isArchived); toast(c.isArchived ? "เผยแพร่กลับแล้ว" : "ซ่อนตัวละครแล้ว (แชทเดิมยังคุยต่อได้)", "success"); reload(); } catch (e) { toast(e.message, "error"); }
  };
  const name = profile?.displayName;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <div className="row mb-16">
        <Button size="sm" icon variant="ghost" aria-label="ย้อนกลับ" onClick={() => navigate(-1)}><Icon name="back" /></Button>
        <div className="grow" />
        <Button size="sm" icon variant="ghost" aria-label={t("แชร์")} onClick={() => setShare(true)}><Icon name="send" /></Button>
        <Button size="sm" icon variant="ghost" aria-label={isSaved ? "ยกเลิกบันทึก" : "บันทึกตัวละคร"} aria-pressed={isSaved} onClick={save}><Icon name="book" filled={isSaved} /></Button>
        <Button size="sm" icon variant="ghost" aria-label={isLiked ? "เลิกถูกใจ" : "ถูกใจ"} aria-pressed={isLiked} onClick={like} style={isLiked ? { color: "var(--accent)" } : undefined}><Icon name="heart" filled={isLiked} /></Button>
        {!isOwner ? <Button size="sm" icon variant="ghost" aria-label={t("รายงาน")} onClick={() => setReport(true)}><Icon name="shield" /></Button> : null}
      </div>

      <Gallery c={c} />

      <div className="mt-16">
        <div className="row wrap" style={{ gap: 8 }}>
          <h1 className="grow">{c.name}</h1>
          {c.status && !["approved", "published", "active"].includes(c.status) ? <span className="pill pill--warn">{statusLabel(c.status)}</span> : null}
          {c.isArchived ? <span className="pill">ซ่อนอยู่</span> : null}
        </div>
        {c.creator ? (
          <div className="row mt-8 wrap">
            <Link to={`/creator/${c.creator.id}`} className="row" style={{ gap: 8 }}><Avatar name={c.creator.displayName} size={26} />โดย @{c.creator.username || c.creator.displayName}</Link>
            {!isOwner ? <Button size="sm" variant={following ? "ghost" : "primary"} aria-pressed={following} onClick={follow}>{following ? t("กำลังติดตาม") : t("ติดตาม")}</Button> : null}
            <span className="faint" style={{ fontSize: 12 }}>สร้างเมื่อ {dateOf(c.createdAt)}</span>
          </div>
        ) : null}
        <p className="mt-16">{personalize(c.tagline, name)}</p>
        <div className="chip-row mt-16" style={{ flexWrap: "wrap" }}>
          {FLAG_LABEL[c.flag] && !(c.tags || []).includes(FLAG_LABEL[c.flag]) ? <Chip size="sm" className={`tag--${c.flag}`}>{FLAG_LABEL[c.flag]}</Chip> : null}
          {(c.tags || []).map((x) => <Chip key={x} size="sm">{x}</Chip>)}
        </div>
        <div className="stats card mt-16">
          <div className="stat"><b>{fmtNum(likeCount)}</b><span>{t("ไลก์")}</span></div>
          <div className="stat"><b>{fmtNum(c.chatCount)}</b><span>{t("แชท")}</span></div>
          <div className="stat"><b>{fmtNum(c.messageCount)}</b><span>{t("ข้อความ")}</span></div>
          <div className="stat"><b>{fmtNum(c.giftCount || 0)}</b><span>{t("ของขวัญ")}</span></div>
        </div>
        {c.shareCode ? <div className="faint mt-8" style={{ fontSize: 12 }}>โค้ดแชร์: <b style={{ letterSpacing: 2 }}>{c.shareCode}</b></div> : null}
        <Button variant="primary" size="lg" block className="mt-16" onClick={start} disabled={starting}>{starting ? "กำลังเปิดห้องแชท..." : t("เริ่มแชท")}</Button>
        {isOwner ? (
          <div className="row mt-8 wrap">
            <Link className="btn grow" to={`/create?edit=${c.id}`}>แก้ไขตัวละคร</Link>
            <Button className="grow" onClick={toggleArchive}>{c.isArchived ? "เผยแพร่กลับ" : "ซ่อนตัวละคร"}</Button>
          </div>
        ) : null}
      </div>

      <div className="mt-24">
        <Tabs variant="line" value={tab} onChange={setTab} tabs={[{ value: "info", label: t("ข้อมูล") }, { value: "gift", label: t("อันดับสายเปย์") }, { value: "comments", label: `${t("ความคิดเห็น")}${commentCount !== null ? ` (${commentCount})` : ""}` }, { value: "clips", label: t("คลิปสั้น") }, { value: "posts", label: t("โพสต์") }]} />
        <Card className="mt-16">
          {tab === "info" ? (
            <div className="stack">
              {c.bio ? <div><div className="strong">ประวัติตัวละคร</div><p className="muted mt-8" style={{ whiteSpace: "pre-wrap" }}>{personalize(c.bio, name)}</p></div> : null}
              <div><div className="strong">คำทักทายเริ่มต้น</div><p className="muted mt-8" style={{ whiteSpace: "pre-wrap" }}>{personalize(personalize(c.greeting, name).replaceAll("{{char}}", c.name), name)}</p>{c.greetingImage ? <img src={c.greetingImage} alt="" style={{ borderRadius: 12, maxHeight: 260, marginTop: 8 }} /> : null}</div>
              {isOwner && c.description ? <div><div className="strong">คำอธิบายสำหรับ AI (เห็นเฉพาะเจ้าของ)</div><p className="muted mt-8" style={{ whiteSpace: "pre-wrap" }}>{c.description}</p></div> : null}
              {c.reviewNote && isOwner ? <div className="card"><b>หมายเหตุจากผู้ดูแล:</b> {c.reviewNote}</div> : null}
            </div>
          ) : tab === "gift" ? <GiftRanking id={id} />
            : tab === "comments" ? <Comments targetType="character" targetId={id} ownerId={c.creatorId} meId={meId} isSuper={isSuperUser} onCount={setCommentCount} />
            : tab === "clips" ? <PostsTab id={id} type="short" meId={meId} isSuper={isSuperUser} />
            : <PostsTab id={id} type="post" meId={meId} isSuper={isSuperUser} />}
        </Card>
      </div>
      <p className="faint center mt-24" style={{ fontSize: 12 }}>ทุกสิ่งที่ตัวละครพูดเป็นการแต่งเรื่อง</p>
      <ShareModal open={share} onClose={() => setShare(false)} character={c} />
      <ReportModal open={report} onClose={() => setReport(false)} targetType="character" targetId={id} title="รายงานตัวละคร" />
    </div>
  );
}
